import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { FeeStructureOption, FeeType, BillingFrequency } from "../interface/profile/FeeStructureOption";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { X } from "lucide-react";

interface ManualFeeFormProps {
  role: 'advisor' | 'analyst';
  initialFee?: Partial<FeeStructureOption>;
  onSave: (fee: FeeStructureOption) => void;
  onCancel: () => void;
}

export function ManualFeeForm({ role, initialFee, onSave, onCancel }: ManualFeeFormProps) {
  const [fee, setFee] = useState<Partial<FeeStructureOption>>(
    initialFee || {
      feeType: undefined,
      name: "",
    }
  );

  const handleSave = () => {
    if (!fee.name || !fee.feeType) return;

    const feeToSave: FeeStructureOption = {
      id: initialFee?.id || `fee-${Date.now()}`,
      feeType: fee.feeType!,
      name: fee.name,
      hourlyRate: fee.hourlyRate,
      flatFeeAmount: fee.flatFeeAmount,
      aumPercentage: fee.aumPercentage,
      performanceFeePercentage: fee.performanceFeePercentage,
      minimumFee: fee.minimumFee,
      billingFrequency: fee.billingFrequency,
      description: fee.description,
    };

    onSave(feeToSave);
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
      <div className="flex items-center justify-between">
        <Label className="text-base">
          {initialFee?.id ? "Edit Fee Structure" : "Add Custom Fee Structure"}
        </Label>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Fee Name */}
      <div className="space-y-2">
        <Label htmlFor="feeName">Service Name *</Label>
        <Input
          id="feeName"
          placeholder={
            role?.toLowerCase() === "advisor"
              ? "e.g., Comprehensive Wealth Management"
              : "e.g., Sector Deep-Dive Analysis, Stock Valuation Report"
          }
          value={fee.name || ""}
          onChange={(e) => setFee({ ...fee, name: e.target.value })}
        />
      </div>

      {/* Fee Type */}
      <div className="space-y-2">
        <Label htmlFor="feeType">Pricing Model *</Label>
        <Select
          value={fee.feeType}
          onValueChange={(value) =>
            setFee({
              ...fee,
              feeType: value as FeeStructureOption["feeType"],
            })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select pricing model" />
          </SelectTrigger>
          <SelectContent>
            {role?.toLowerCase() === "advisor" && (
              <>
                <SelectItem value="AUM_BASED">AUM-Based (% of Assets)</SelectItem>
                <SelectItem value="FLAT_FEE">Flat Fee</SelectItem>
                <SelectItem value="HOURLY">Hourly Rate</SelectItem>
                <SelectItem value="PERFORMANCE_BASED">Performance-Based</SelectItem>
                <SelectItem value="HYBRID">Hybrid Model</SelectItem>
              </>
            )}
            {role?.toLowerCase() === "analyst" && (
              <>
                <SelectItem value="HOURLY">Hourly Rate</SelectItem>
                <SelectItem value="FLAT_FEE">Per Project/Report</SelectItem>
                <SelectItem value="AUM_BASED">Monthly Retainer</SelectItem>
                <SelectItem value="PERFORMANCE_BASED">Performance-Based</SelectItem>
                <SelectItem value="HYBRID">Hybrid Model</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Dynamic Pricing Fields */}
      {fee.feeType && (
        <div className="grid grid-cols-2 gap-4">
          {(fee.feeType === "HOURLY" || fee.feeType === "HYBRID") && (
            <div className="space-y-2">
              <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
              <Input
                id="hourlyRate"
                type="number"
                placeholder={role?.toLowerCase() === "advisor" ? "e.g., 250" : "e.g., 150"}
                value={fee.hourlyRate || ""}
                onChange={(e) =>
                  setFee({
                    ...fee,
                    hourlyRate: parseFloat(e.target.value) || undefined,
                  })
                }
              />
            </div>
          )}

          {(fee.feeType === "FLAT_FEE" || fee.feeType === "HYBRID") && (
            <div className="space-y-2">
              <Label htmlFor="flatFeeAmount">
                {role?.toLowerCase() === "advisor" ? "Flat Fee ($)" : "Project/Report Fee ($)"}
              </Label>
              <Input
                id="flatFeeAmount"
                type="number"
                placeholder={role?.toLowerCase() === "advisor" ? "e.g., 5000" : "e.g., 2500"}
                value={fee.flatFeeAmount || ""}
                onChange={(e) =>
                  setFee({
                    ...fee,
                    flatFeeAmount: parseFloat(e.target.value) || undefined,
                  })
                }
              />
            </div>
          )}

          {(fee.feeType === "AUM_BASED" || fee.feeType === "HYBRID") && (
            <div className="space-y-2">
              <Label htmlFor="aumPercentage">
                {role?.toLowerCase() === "advisor" ? "AUM Percentage (%)" : "Monthly Retainer ($)"}
              </Label>
              <Input
                id="aumPercentage"
                type="number"
                step={role?.toLowerCase() === "advisor" ? "0.1" : "1"}
                placeholder={role?.toLowerCase() === "advisor" ? "e.g., 1.0" : "e.g., 3000"}
                value={fee.aumPercentage || ""}
                onChange={(e) =>
                  setFee({
                    ...fee,
                    aumPercentage: parseFloat(e.target.value) || undefined,
                  })
                }
              />
            </div>
          )}

          {(fee.feeType === "PERFORMANCE_BASED" || fee.feeType === "HYBRID") && (
            <div className="space-y-2">
              <Label htmlFor="performanceFee">Performance Fee (%)</Label>
              <Input
                id="performanceFee"
                type="number"
                step="1"
                placeholder="e.g., 20"
                value={fee.performanceFeePercentage || ""}
                onChange={(e) =>
                  setFee({
                    ...fee,
                    performanceFeePercentage: parseFloat(e.target.value) || undefined,
                  })
                }
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="minimumFee">Minimum Fee ($) (Optional)</Label>
            <Input
              id="minimumFee"
              type="number"
              placeholder={role?.toLowerCase() === "advisor" ? "e.g., 1000" : "e.g., 500"}
              value={fee.minimumFee || ""}
              onChange={(e) =>
                setFee({
                  ...fee,
                  minimumFee: parseFloat(e.target.value) || undefined,
                })
              }
            />
          </div>
        </div>
      )}

      {/* Billing Frequency for Advisors */}
      {role?.toLowerCase() === "advisor" && fee.feeType && (
        <div className="space-y-2">
          <Label>Billing Frequency (Optional)</Label>
          <div className="flex gap-2">
            {["MONTHLY", "QUARTERLY", "ANNUALLY"].map((freq) => (
              <Button
                key={freq}
                type="button"
                variant={fee.billingFrequency === freq ? "default" : "outline"}
                onClick={() =>
                  setFee({
                    ...fee,
                    billingFrequency: freq as FeeStructureOption["billingFrequency"],
                  })
                }
                className="flex-1"
              >
                {freq}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description (Optional)</Label>
        <Textarea
          id="description"
          placeholder={
            role?.toLowerCase() === "advisor"
              ? "Describe what's included in this fee structure..."
              : "e.g., Includes 15-page report with industry comparison, financial modeling, and 1-hour consultation"
          }
          value={fee.description || ""}
          onChange={(e) => setFee({ ...fee, description: e.target.value })}
          rows={3}
        />
      </div>

      <div className="flex gap-2 pt-2">
        <Button
          type="button"
          onClick={handleSave}
          disabled={!fee.feeType || !fee.name}
          className="flex-1"
        >
          {initialFee?.id ? "Update Fee Structure" : "Add Fee Structure"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
